
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Modal, Typography, Flex, Button, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { isMediaFileVideo } from "utils/media-upload"
import ImageCropper from "ui/ImageCropper"
import VideoTrim from "ui/VideoTrim"
import { FFmpeg } from "@ffmpeg/ffmpeg"
import { fetchFile } from "@ffmpeg/util"
import styles from "./index.module.css"


const { Paragraph, Text } = Typography

/**
 * MediaEditorModal
 * - For images: delegates to ImageCropper (keeps existing UX and logic)
 * - For videos: shows a simple modal with helper text and a video player (placeholder for trimming)
*/
const MediaEditorModal = ({ visible, onClose, media, onImageCropComplete, onVideoConfirm, targetWidth = 1080, targetHeight = 1080 }) => {
  const { t } = useTranslation()
  const _global = typeof window !== "undefined" ? window : {};

  const isVideo = isMediaFileVideo(media)
  const imageSrc = media?.url || media?.preview || media?.file_preview
  const videoSrc = media?.url || media?.preview || media?.file_preview

  // --- Video trim state ---
  const [start, setStart] = useState(0)
  const [end, setEnd] = useState(0)
  const [error, setError] = useState(null)
  const MAX_TRIM = 300 // 5 minutes
  const ffmpegRef = useRef(null)
  const [step, setStep] = useState("trim") // 'trim' | 'cover'
  const [isWorking, setIsWorking] = useState(false)
  // cover video preview state
  const coverVideoRef = useRef(null)
  const frameCanvasRef = useRef(null)
  const previewRef = useRef(null)
  const [coverVideoDuration, setCoverVideoDuration] = useState(0)
  const [coverTime, setCoverTime] = useState(0)
  const [thumbnails, setThumbnails] = useState([])
  const filmstripRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const rafIdRef = useRef(null)
  const [frameA11yUrl, setFrameA11yUrl] = useState(null)

  // Wait for a single 'seeked' event on a given HTMLVideoElement
  const waitForVideoSeekOnce = useCallback((video) => new Promise((resolve) => {
    let done = false
    function onSeeked() {
      if (done) return
      done = true
      video.removeEventListener("seeked", onSeeked)
      resolve()
    }
    video.addEventListener("seeked", onSeeked)
  }), [])

  useEffect(() => {
    if (!visible) return
    // Reset on open/media change
    setError(null)
    setStart(0)
    setEnd(0)
    setStep("trim")
    setCoverVideoDuration(0)
    setCoverTime(0)
    setThumbnails([])
  }, [visible, media])

  const selectedDuration = Math.max(0, end - start)
  useEffect(() => {
    if (selectedDuration > MAX_TRIM) {
      setError(t("listings.tabs.media.max_trim_error"))
    } else {
      setError(null)
    }
  }, [selectedDuration, t])

  // receive updates from VideoTrim
  const handleTrimChange = ({ start: s, end: e }) => {
    if (typeof s === "number") setStart(s)
    if (typeof e === "number") setEnd(e)
  }

  const ensureFfmpeg = async () => {
    if (!ffmpegRef.current) {
      const ff = new FFmpeg()
      await ff.load()
      ffmpegRef.current = ff
    }
    return ffmpegRef.current
  }

  // (Removed cover image cropping flow)

  // Capture current frame from <video> element and draw to main canvas
  const captureCurrentFrame = async () => {
    const videoEl = coverVideoRef.current
    const canvas = frameCanvasRef.current
    if (!videoEl?.videoWidth || !canvas) return null
    // size canvas to natural frame
    canvas.width = videoEl.videoWidth
    canvas.height = videoEl.videoHeight
    const ctx = canvas.getContext("2d")
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92))
    const url = blob ? URL.createObjectURL(blob) : null
    // Ensure loader is visible for at least 1 second
    if (url) {
      setTimeout(() => {
        setFrameA11yUrl(url)
      }, 1000)
    }
    return { blob, url }
  }

  // Cleanup any pending animation frames
  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [])

  // Generate filmstrip thumbnails sequentially (only in cover step)
  useEffect(() => {
    let cancelled = false
    if (!visible || step !== "cover" || !videoSrc || !coverVideoDuration) return
    const count = 24
    const thumbStep = coverVideoDuration / count
    const off = document.createElement("video")
    off.crossOrigin = "anonymous"
    off.preload = "metadata"
    off.muted = true
    off.playsInline = true
    off.src = videoSrc
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    const waitForSeek = (video) => new Promise((resolve) => {
      video.addEventListener("seeked", resolve, { once: true })
    })

    const waitForLoadedMetadata = (video) => new Promise((resolve) => {
      video.addEventListener("loadedmetadata", resolve, { once: true })
    })

    const doSeek = async (t) => {
      try {
        off.currentTime = t
      } catch (err) {
        // Handle browsers that may throw on invalid seeks (e.g., not ready yet)
        // We log for visibility and bail out gracefully so the loop can continue.
        console.warn("MediaEditorModal: failed to set currentTime during thumbnail seek", err)
        return
      }
      await waitForSeek(off)
    }

    const run = async () => {
      await waitForLoadedMetadata(off)
      const frames = []
      for (let i = 0; i < count; i++) {
        if (cancelled) break
        const t = Math.min(coverVideoDuration, Math.max(0, i * thumbStep))
        await doSeek(t)
        // draw a small thumbnail to reduce CPU/memory
        canvas.width = 60
        canvas.height = 40
        ctx.drawImage(off, 0, 0, canvas.width, canvas.height)
        const url = canvas.toDataURL("image/jpeg", 0.7)
        const item = { time: t, url }
        frames.push(item)
        // Update progressively every few frames to reduce re-renders
        if ((i + 1) % 4 === 0 && !cancelled) {
          setThumbnails(frames.slice())
        }
      }
      if (!cancelled) setThumbnails(frames)
    }
    run()
    return () => { cancelled = true }
  }, [visible, step, videoSrc, coverVideoDuration])

  // Position of scrub handle over filmstrip
  const handleLeftPercent = useMemo(() => (coverVideoDuration ? (coverTime / coverVideoDuration) * 100 : 0), [coverTime, coverVideoDuration])

  // Current thumbnail URL for handle background
  const currentThumbUrl = useMemo(() => {
    if (!thumbnails?.length) return undefined
    let best = thumbnails[0]
    let bestDiff = Math.abs((thumbnails[0]?.time || 0) - coverTime)
    for (let i = 1; i < thumbnails.length; i++) {
      const d = Math.abs((thumbnails[i].time || 0) - coverTime)
      if (d < bestDiff) { best = thumbnails[i]; bestDiff = d }
    }
    return best?.url
  }, [thumbnails, coverTime])

  const formatTime = (s) => {
    const mm = Math.floor(s / 60)
    const ss = Math.floor(s % 60)
    return `${mm}:${ss.toString().padStart(2, "0")}`
  }

  // Helpers to map pointer position to time
  const timeFromClientX = useCallback((clientX) => {
    const el = filmstripRef.current
    if (!el || !coverVideoDuration) return 0
    const rect = el.getBoundingClientRect()
    const x = Math.min(rect.right, Math.max(rect.left, clientX)) - rect.left
    const pct = rect.width > 0 ? x / rect.width : 0
    return pct * coverVideoDuration
  }, [coverVideoDuration])

  const updateTimeFromClientX = useCallback((clientX) => {
    const t = timeFromClientX(clientX)
    setCoverTime(t)
    if (coverVideoRef.current) {
      try {
        coverVideoRef.current.currentTime = t
      } catch (err) {
        console.warn("MediaEditorModal: failed to set currentTime from pointer update", err)
      }
    }
  }, [timeFromClientX])

  // Global drag listeners
  useEffect(() => {
    if (!dragging) return
    const onMove = (e) => {
      if (e.touches?.length) {
        updateTimeFromClientX(e.touches[0].clientX)
      } else {
        updateTimeFromClientX(e.clientX)
      }
    }
    const onUp = () => setDragging(false)
    _global.addEventListener("mousemove", onMove)
    _global.addEventListener("mouseup", onUp)
    _global.addEventListener("touchmove", onMove, { passive: false })
    _global.addEventListener("touchend", onUp)
    return () => {
      _global.removeEventListener("mousemove", onMove)
      _global.removeEventListener("mouseup", onUp)
      _global.removeEventListener("touchmove", onMove)
      _global.removeEventListener("touchend", onUp)
    }
  }, [dragging, updateTimeFromClientX])

  const trimVideo = async () => {
    if (!videoSrc || end <= start) return null
    let ffmpeg, data, ext, inName, outName, bytes, blob, url
    try {
      ffmpeg = await ensureFfmpeg()
      data = await fetchFile(videoSrc)
      ext = typeof videoSrc === "string" && videoSrc.includes(".") ? videoSrc.split(".").pop().split("?")[0] : "mp4"
      inName = `input.${ext}`
      outName = `out.mp4`
      await ffmpeg.writeFile(inName, data)
    } catch (err) {
      setError("Failed to load or write video file. Please check the file source and try again.")
      console.error("MediaEditorModal: FS error during video load/write", err)
      return null
    }

    const ss = Math.max(0, start).toFixed(2)
    const t = Math.max(0.01, end - start).toFixed(2)

    const runTrim = async (args) => ffmpeg.exec(args)
    let ok = true
    try {
      await runTrim(["-ss", ss, "-t", t, "-i", inName, "-c", "copy", outName])
    } catch (err) {
      console.warn("MediaEditorModal: fast trim failed, falling back to re-encode", err)
      ok = false
    }
    if (!ok) {
      try {
        await runTrim(["-ss", ss, "-t", t, "-i", inName, "-c:v", "libx264", "-c:a", "aac", "-movflags", "+faststart", outName])
      } catch (err) {
        setError("Failed to trim video. Please try again or use a different file.")
        console.error("MediaEditorModal: FS error during video trim", err)
        return null
      }
    }

    // Read trimmed video
    try {
      bytes = await ffmpeg.readFile(outName)
      blob = new Blob([bytes.buffer ?? bytes], { type: "video/mp4" })
      url = URL.createObjectURL(blob)
    } catch (err) {
      setError("Failed to read trimmed video file. Please try again.")
      console.error("MediaEditorModal: FS error during video read", err)
      return null
    }

    // Generate poster image from trimmed video (first second)
    const posterName = "poster.jpg"
    try {
      await ffmpeg.exec(["-ss", "0.5", "-i", outName, "-frames:v", "1", "-vf", "scale=480:-1", posterName])
      const pbytes = await ffmpeg.readFile(posterName)
      const pblob = new Blob([pbytes.buffer ?? pbytes], { type: "image/jpeg" })
      const posterUrl = URL.createObjectURL(pblob)
      return { blob, url, posterUrl, posterBlob: pblob }
    } catch (err) {
      // If poster generation fails, still return video
      setError("Poster generation failed. Video saved without poster image.")
      console.warn("MediaEditorModal: poster generation failed; returning video without poster", err)
      return { blob, url }
    }
  }

  // IMAGE MODE: reuse existing ImageCropper (it owns its Modal)
  if (!isVideo) {
    if (!imageSrc) return null
    return (
      <ImageCropper
        visible={visible}
        onClose={onClose}
        imageSrc={imageSrc}
        onCropComplete={onImageCropComplete}
        targetWidth={targetWidth}
        targetHeight={targetHeight}
      />
    )
  }


  // Save current frame (no cropping) as poster and confirm
  const saveCoverFrame = async () => {
    setIsWorking(true)
    try {
      // Ensure we capture exactly at the selected time

      const ensureAtTime = async () => {
        const v = coverVideoRef.current
        if (!v) return
        try {
          v.currentTime = Math.min(Math.max(0, coverTime), coverVideoDuration || coverTime)
        } catch (err) {
          console.warn("MediaEditorModal: failed to set currentTime before capturing cover frame", err)
          return
        }
        await waitForVideoSeekOnce(v)
      }
      await ensureAtTime()
      // Capture current frame from the video element
      let frame = await captureCurrentFrame()
      // If CORS prevents capture, fall back to ffmpeg grabbing a frame
      if (!frame?.blob) {
        try {
          const ffmpeg = await ensureFfmpeg()
          const data = await fetchFile(videoSrc)
          const ext = typeof videoSrc === "string" && videoSrc.includes(".") ? videoSrc.split(".").pop().split("?")[0] : "mp4"
          const inName = `cover_src.${ext}`
          const outName = `cover_frame.jpg`
          await ffmpeg.writeFile(inName, data)
          await ffmpeg.exec(["-ss", `${Math.max(0, coverTime).toFixed(2)}`, "-i", inName, "-frames:v", "1", outName])
          const bytes = await ffmpeg.readFile(outName)
          const blob = new Blob([bytes.buffer ?? bytes], { type: "image/jpeg" })
          frame = { blob, url: URL.createObjectURL(blob) }
        } catch (err) {
          console.warn("MediaEditorModal: ffmpeg fallback frame extraction failed", err)
        }
      }
      const posterBlob = frame?.blob
      const posterUrl = frame?.url
      const result = (await trimVideo()) || {}
      if (onVideoConfirm) {
        onVideoConfirm({ media, start, end, ...result, posterUrl, posterBlob })
      }
      onClose()
    } finally {
      setIsWorking(false)
    }
  }

  // Render modal with either Trim UI or Cover UI
  return (
    <Modal
      title={step === "trim" ? t("listings.tabs.media.video_title") : (t?.("listings.tabs.media.cover.title") || "Choose Cover Image")}
      open={visible}
      onCancel={onClose}
      footer={
        <Flex justify="start" gap={8}>
          <Button onClick={onClose}>{t("global.cancel")}</Button>
          {step === "trim" ? (
            <Button
              type="primary"
              loading={isWorking}
              disabled={!!error || selectedDuration <= 0}
              onClick={async () => {
                // Switch content to cover crop within the same modal
                setIsWorking(true)
                try {
                  // Default: jump to middle of selected range
                  const mid = Math.max(0, start + Math.max(0, end - start) / 2)
                  setCoverTime(mid)
                  setStep("cover")
                } finally {
                  setIsWorking(false)
                }
              }}
            >
              {t("global.next")}
            </Button>
          ) : (
            <Button type="primary" loading={isWorking} onClick={saveCoverFrame}>
              {t("global.save")}
            </Button>
          )}
        </Flex>
      }
      width={900}
    >
      {step === "trim" ? (
        <Flex vertical gap={16}>
          <Paragraph>
            <Text type="secondary">{t("listings.tabs.media.video_trim_placeholder")}</Text>
          </Paragraph>
          {videoSrc ? (
            <VideoTrim src={videoSrc} maxTrim={MAX_TRIM} onChange={handleTrimChange} />
          ) : (
            <Text type="secondary">{t("listings.tabs.media.no_video_source")}</Text>
          )}
        </Flex>
      ) : (
        <Flex vertical gap={12}>
          <Paragraph className={styles.noMarginBottom}>
            <Text type="secondary">{t?.("listings.tabs.media.cover.subtitle") || "Choose cover image for the video."}</Text>
          </Paragraph>
          <div className={styles.previewContainer} ref={previewRef}>
            {/* hidden video only for extracting frame */}
            <video
              ref={coverVideoRef}
              src={videoSrc || undefined}
              crossOrigin="anonymous"
              className={styles.hiddenVideo}
              onLoadedMetadata={(e) => {
                const d = e.currentTarget.duration || 0
                setCoverVideoDuration(d)
                const mid = Math.max(0, start + Math.max(0, end - start) / 2)
                const initial = Number.isFinite(mid) && mid > 0 ? Math.min(d || mid, mid) : 0
                setCoverTime(initial)
                try { e.currentTarget.currentTime = initial } catch (err) {
                  console.warn("MediaEditorModal: failed to set currentTime on metadata load", err)
                }
                // draw once metadata ready
                setTimeout(() => { captureCurrentFrame() }, 0)
              }}
              onTimeUpdate={(e) => {
                const t = e.currentTarget.currentTime || 0
                setCoverTime(t)
                if (rafIdRef.current == null) {
                  rafIdRef.current = _global.requestAnimationFrame(() => {
                    rafIdRef.current = null
                    captureCurrentFrame()
                  })
                }
              }}
            >
              <track
                kind="captions"
                srcLang="en"
                label="English"
                src="data:text/vtt;charset=utf-8,WEBVTT%0A%0A"
                default
              />
            </video>
            {/* main frame canvas */}
            <canvas
              ref={frameCanvasRef}
              className={styles.frameCanvas}
            />
            {/* accessible image alternative for screen readers */}
            {frameA11yUrl ? (
              <img
                src={frameA11yUrl}
                alt={`Video frame preview at ${formatTime(coverTime)}`}
                className={styles.visuallyHidden}
              />
            ) : (
              <Flex justify="center" align="center" className={styles.loadingOverlay}>
                <Spin size="large" tip="Loading preview..." />
              </Flex>
            )}
            {/* time overlay */}
            <div className={styles.timeOverlay}>{formatTime(coverTime)}</div>
            {/* overlay crop outline centered based on aspect */}
            {(() => {
              const videoEl = coverVideoRef.current
              const container = previewRef.current
              if (!videoEl?.videoWidth || !container) return null
              const containerWidth = container.clientWidth
              const containerHeight = Math.round(containerWidth * (videoEl.videoHeight / videoEl.videoWidth))
              const aspect = targetWidth && targetHeight ? targetWidth / targetHeight : 1
              let cropW, cropH
              if (Math.abs(aspect - 1) < 0.05) { // square
                const size = Math.min(containerWidth * 0.85, containerHeight * 0.85)
                cropW = size; cropH = size
              }
              const left = (containerWidth - cropW) / 2
              const top = (containerHeight - cropH) / 2
              return <div className={styles.cropOutline} style={{ left, top, width: cropW, height: cropH }} />
            })()}
          </div>
          {/* filmstrip */}
          {currentThumbUrl &&
            <button
              ref={filmstripRef}
              className={styles.filmstripWrap}
              type="text"
              aria-label={t?.("listings.tabs.media.cover.timeline") || "Video timeline"}
              onClick={(e) => {
                updateTimeFromClientX(e.clientX)
              }}
              onMouseDown={(e) => {
                setDragging(true)
                updateTimeFromClientX(e.clientX)
              }}
              onTouchStart={(e) => {
                setDragging(true)
                if (e.touches?.length) updateTimeFromClientX(e.touches[0].clientX)
              }}
            >
              <div className={styles.filmstripBar}>
                {thumbnails.map((th) => (
                  <img key={th.time} className={styles.thumb} src={th.url} alt="thumb" />
                ))}
              </div>
              <img
                className={`${styles.scrubHandle} ${dragging ? styles.dragging : ""}`}
                style={{ left: `${handleLeftPercent}%` }}
                src={currentThumbUrl || undefined}
                alt={step === "cover" 
                  ? `Cover frame preview at ${formatTime(coverTime)}` 
                  : `Selected frame at ${formatTime(coverTime)}`}
              />
              {/* Accessible native range input overlay for keyboard and screen readers */}
              <input
                type="range"
                min={0}
                max={Math.max(0, Math.floor(coverVideoDuration || 0))}
                step={0.01}
                value={coverTime}
                aria-label={t?.("listings.tabs.media.cover.scrubber") || "Video cover position"}
                onChange={(e) => {
                  const val = Number(e.target.value) || 0
                  setCoverTime(val)
                  if (coverVideoRef.current) {
                    try { coverVideoRef.current.currentTime = val } catch (err) {
                      console.warn("MediaEditorModal: failed to set currentTime from range input", err)
                    }
                  }
                }}
                onInput={(e) => {
                  const val = Number(e.currentTarget.value) || 0
                  setCoverTime(val)
                  if (coverVideoRef.current) {
                    try { coverVideoRef.current.currentTime = val } catch (err) {
                      console.warn("MediaEditorModal: failed to set currentTime from range input (onInput)", err)
                    }
                  }
                }}
                className={styles.a11yRange}
              />
              <div className={styles.timestamps}>
                {formatTime(coverTime)} / {formatTime(coverVideoDuration || 0)}
              </div>
            </button>}
        </Flex>
      )}
    </Modal>
  )
}

MediaEditorModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  media: PropTypes.object, // image or video media object
  onImageCropComplete: PropTypes.func, // only used for images
  onVideoConfirm: PropTypes.func, // optional: called when clicking Next in video mode
  targetWidth: PropTypes.number,
  targetHeight: PropTypes.number
}

export default MediaEditorModal
